import { defineStore } from 'pinia'
import { ref } from 'vue'
import { sdk } from '@/lib/sdk'
import type { ResourceData } from '@/types/resource'

export const useResourceStore = defineStore('resource', () => {
    const resourceData = ref<ResourceData | null>(null)
    const resourceHash = ref<string | null>(null)
    const isLoaded = ref(false)

    /**
     * Setup CDN resources with hash-based caching
     * This function loads static data from CDN and caches it in localStorage
     *
     * @param hash - The resource hash from init response
     */
    async function setupCdn(hash: string) {
        // Get the old resource hash if exists
        const localResourceHash = localStorage.getItem('resourceHash')
        let resource: ResourceData | null = null

        // Check if we need to fetch new resources
        if (localResourceHash !== hash || localResourceHash == null) {

            try {
                const response = await sdk.staticData.getResources(hash)

                if (response) {
                    resource = response

                    // Save to localStorage for caching
                    localStorage.setItem('resourceData', JSON.stringify(resource))
                    localStorage.setItem('resourceHash', hash)
                }
            } catch (error) {
                console.error('Failed to fetch CDN resources:', error)

                // Fallback to local cached data if available
                const cachedResourceString = localStorage.getItem('resourceData')
                if (cachedResourceString) {
                    resource = JSON.parse(cachedResourceString)
                }
            }
        } else {
            // Unpack the local resource
            const resourceString = localStorage.getItem('resourceData')
            if (resourceString) {
                resource = JSON.parse(resourceString)
            }
        }

        // Load resources into the store
        if (resource) {
            resourceData.value = resource
            resourceHash.value = hash
            isLoaded.value = true
        }

        return resource
    }

    /**
     * Clear all cached resources
     */
    function clearResources() {
        resourceData.value = null
        resourceHash.value = null
        isLoaded.value = false
        localStorage.removeItem('resourceHash')
        localStorage.removeItem('resourceData')
    }

    /**
     * Get a specific resource value by key
     */
    function getResource<T = any>(key: string): T | undefined {
        if (!resourceData.value) return undefined
        return resourceData.value[key] as T
    }

    /**
     * Get CDN URL for a given path
     * @param path - The path to the resource (e.g., 'images/connect/logos/connect-care.svg')
     */
    function getCdnUrl(path: string): string {
        const cdnBaseUrl = import.meta.env.VITE_CDN_BASE_URL
        // Remove leading slash if present to avoid double slashes
        const cleanPath = path.startsWith('/') ? path.slice(1) : path
        return `${cdnBaseUrl}/${cleanPath}`
    }

    return {
        // State
        resourceData,
        resourceHash,
        isLoaded,

        // Actions
        setupCdn,
        clearResources,
        getResource,
        getCdnUrl
    }
})
